import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Edit, Trash2, UserIcon, FileText, Link2, Clock, ExternalLink, Plus, Monitor, MoreVertical, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { ActivityLink, ActivityLogEntry, System, Activity, User as UserType, Procedure, JobRole, PersonType, ActivityRaci, RaciRole, PartyType, RaciParty } from "../types";
import { Textarea } from "../components/ui/textarea";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { MultiSelect } from "../components/ui/multi-select";
import CustomEditor from "../components/ui/custom-text-editor";
import FileAttachments from "../components/FileAttachments";
import ActivityLog from "../components/Activities/ActivityLog";
import { LoadingPageSkeleton } from "../components/common/LoadingPageSkeleton";
import { userHasAccessToDepartment, canUserViewProcedure } from "../utils/procedure-permissions";
import { getDepartmentIdByUser, toSydneyDate } from "../utils/helpers";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Can } from "../components/Can";
import { useAbility } from '@casl/react';
import { AbilityContext } from '../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import api from "../utils/api";

const getActivity = async (id: any) => {
  const data = await api.get(`/api/activities/${id}`);
  return data.activity || null;
}

// type RaciRole = 'responsiblePerson' | 'accountablePerson' | 'consultedPerson' | 'informedPerson';

// // Use a more specific type for the entity type if possible
// type PartyType = 'User' | 'JobRole' | 'ExternalPartner';

// interface RaciParty {
//   id: number | string;
//   type: PartyType;
// }

// type RaciObject = {
//   [K in RaciRole]?: RaciParty;
// };

export default function ActivityPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { users, procedures, activities, updateActivity, systems, jobRoles, addActivityLink, deleteActivityLink, updateActivityLink } = useData();
  const { currentUser } = useAuth();
  const ability = useAbility(AbilityContext);

  const [activity, setActivity] = useState<Activity | null>(null);
  const [editActivity, setEditActivity] = useState<Activity | null>(null);
  const [procedure, setProcedure] = useState<Procedure | null>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [openAddLink, setOpenAddLink] = useState(false);
  const [newLink, setNewLink] = useState<{ title: string; url: string; description: string }>({
    title: "",
    url: "",
    description: ""
  });
  const [editDescription, setEditDescription] = useState(false);
  const [description, setDescription] = useState("");
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [editSystems, setEditSystems] = useState(false);
  const [openEditLink, setOpenEditLink] = useState(false);
  const [editingLink, setEditingLink] = useState<ActivityLink | null>(null);
  const [editHowTo, setEditHowTo] = useState(false);
  const [howTo, setHowTo] = useState("");
  const [externalPartners, setExternalPartners] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>("activity");
  const [hasActivity, setHasActivity] = useState<boolean>(true);
  const [raci, setRaci] = useState<ActivityRaci | undefined>({});

  useEffect(() => {
    const fetchActivity = async (id: any) => {
      if (id) {
        // const foundActivity = activities.find(a => a.id == id);
        // if (foundActivity) {
        //   setHasActivity(true);
        //   setActivity(foundActivity);
        //   setEditActivity(foundActivity);
        //   // setRaci(foundActivity?.raci);
        // } else {
        const foundActivity = await getActivity(id);

        if (!foundActivity) {
          setHasActivity(false);

        } else {
          setHasActivity(true);
          setActivity(foundActivity);
          setEditActivity(foundActivity);
          setDescription(foundActivity.description);
          setHowTo(foundActivity.howTo);
          setSelectedSystems(foundActivity.systemsUsed || []);
          setProcedure(foundActivity.procedure);
          // setRaci(foundActivity?.raci);
        }

        // }

        // if (activity) {
        //   setHasActivity(true);
        //   setDescription(activity.description || "");
        //   setSelectedSystems(activity.systemsUsed || []);
        //   setHowTo(activity.howTo || "");

        //   setProcedure(typeof activity.procedure !== 'string' ? activity.procedure : null);
        // } 
      } else {
        setHasActivity(false);
      }
    }

    fetchActivity(id);
  }, [id, activities, procedures]);

  useEffect(() => {
    if (editActivity?.raci) {
      setRaci(editActivity.raci);
    } else {
      setRaci({}); // Reset if there's no activity
    }
  }, [editActivity]);

  useEffect(() => {
    if (activity) {
      setProcedure(typeof activity.procedure !== 'string' ? activity.procedure : null);
      setAttachments(activity.attachments || []);
    }
  }, [activity]);

  // Fetch external partners
  useEffect(() => {
    const fetchExternalPartners = async () => {
      try {
        const data = await api.get("/api/external-partners");
        setExternalPartners(data.filter((p: any) => p.active));
      } catch (error) {
        console.error('Error fetching external partners:', error);
      }
    };

    fetchExternalPartners();
  }, []);

  if (redirect) {
    return <RouterLink to={`/procedure/${procedure?.id}`} />;
  }

  if (!hasActivity) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Activity not found</p>
      </div>
    );
  }

  if (!activity || !procedure) {
    return <LoadingPageSkeleton />;
  }

  if (!canUserViewProcedure(currentUser, procedure, ability)) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>You don't have permission to access this activity</p>
      </div>
    );
  }

  const canEdit = currentUser?.userRole === "Admin" ||
    currentUser?.userRole === "Director" ||
    (currentUser?.userRole === "Manager" && procedure?.department?.id == getDepartmentIdByUser(currentUser)) ||
    currentUser?.jobRole === "HSEQ Manager";

  // Get responsible person
  const getRaciAssignment = (raci: RaciParty | undefined) => {
    if (!raci) return null;

    // If id is a User object, get its _id
    const userId = raci.id;
    const type = raci.type;

    if (type == 'User') {
      const user = users.find(u => u.id == userId);
      if (user) return { type: 'user', data: user };
    }

    if (type == 'JobRole') {
      const role = jobRoles.find(r => r.id == userId);
      if (role) return { type: 'role', data: role };
    }

    if (type == 'ExternalPartner') {
      const partner = externalPartners.find(p => p.id == userId);
      if (partner) return { type: 'partner', data: partner };
    }

    return null;
  };

  // Helper function to render a SINGLE RACI party
  const renderRaciParty = (party: RaciParty | null | undefined) => {
    if (!party) return <span>No one assigned</span>;

    const assignment = getRaciAssignment(party);
    if (!assignment) return <span>Unknown assignment</span>;

    switch (assignment.type) {
      case 'user':
        return (
          <RouterLink to={`/people/${assignment.data.id}`} className="hover:underline">
            {assignment.data.name}
          </RouterLink>
        );
      case 'role':
        return (
          <RouterLink to={`/job-roles/${assignment.data.id}`} className="hover:underline">
            {`${assignment.data.title} (Role)`}
          </RouterLink>
        );
      case 'partner':
        return <span>{`${assignment.data.name} (External)`}</span>;
      default:
        return <span>Unknown assignment</span>;
    }
  };

  // Helper function to render an ARRAY of RACI parties
  const renderRaciArray = (parties: RaciParty[] | undefined) => {
    if (!parties || parties.length === 0) {
      return <span>None</span>;
    }

    return (
      <div className="flex flex-col space-y-1">
        {parties.map((party, index) => (
          <div key={index} className="flex items-center gap-2">
            <UserIcon size={16} />
            {renderRaciParty(party)}
          </div>
        ))}
      </div>
    );
  };



  // Helper function to get the ID from a RACI field value
  const getRaciId = (value?: string | UserType | null) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value.id;
  };

  // const responsibleAssignment = getRaciAssignment(activity.raci?.responsiblePerson);
  // const accountableAssignment = getRaciAssignment(activity.raci?.accountablePerson);
  // const consultedAssignment = getRaciAssignment(activity.raci?.consultedPerson);
  // const informedAssignment = getRaciAssignment(activity.raci?.informedPerson);
  const creator = typeof activity.createdBy !== 'string' ? activity.createdBy : { name: activity.createdBy };

  // Filter departmental users for activity assignment
  const departmentalUsers = users.filter(user => {
    if (currentUser?.userRole === "Admin" || currentUser?.userRole === "Director" || currentUser?.jobRole === "HSEQ Manager") {
      return true; // Show all users for Admin and Director
    } else {
      return user.department == procedure.departmentId;
    }
  });

  const filteredUsers = users.filter(user => {
    // Archived users cannot be assigned RACI roles
    if (user.archived) return false;

    const userDepartmentId = getDepartmentIdByUser(user);
    if (currentUser?.userRole === "Admin" || currentUser?.userRole === "Director" || currentUser?.jobRole === "HSEQ Manager") {
      return true; // Show all users
    } else {
      return userDepartmentId == getDepartmentIdByUser(currentUser as UserType);
    }
  });

  // Filter active systems
  const activeSystems = systems?.filter(system => system.active) || [];

  const handleRaciSelect = (role: RaciRole, value: string | string[]) => {
    // Handle roles that accept multiple parties (an array)
    if (role === 'consultedPerson' || role === 'informedPerson') {
      if (Array.isArray(value)) {
        const parties: RaciParty[] = value.map(v => {
          const [type, id] = v.split('-');
          return { id, type: type as PartyType };
        });

        setRaci(currentRaci => ({
          ...currentRaci,
          [role]: parties,
        }));
      }
      return; // End execution for multi-select roles
    }

    // Handle roles that accept a single party (a string value)
    if (typeof value === 'string') {
      const [type, id] = value.split('-');

      // If "None" is selected, remove the property
      if (id === 'none') {
        setRaci(currentRaci => {
          const { [role]: removed, ...rest } = currentRaci || {};
          return rest;
        });
        return;
      }

      // Otherwise, create the RaciParty object
      const newParty: RaciParty = {
        id: id,
        type: type as PartyType,
      };

      setRaci(currentRaci => ({
        ...currentRaci,
        [role]: newParty,
      }));
    }
  };


  const handleUpdate = async () => {

    if (!editActivity?.activityName || !raci?.responsiblePerson || !raci?.accountablePerson) {
      toast.error("Activity name, at least one responsible person, and accountable person are required");
      return;
    }

    try {
      // Convert "none" values to empty strings for the backend
      const activityToUpdate: Partial<Activity> = {
        ...editActivity,
        raci
      };

      const response = await updateActivity(procedure.id, editActivity.id, activityToUpdate);

      if (response.success) {
        toast.success(`Updated activity successfully!`);
        // setProcedure(response.procedure);
        setOpenEdit(false);
      } else {
        toast.error("Failed to update activity");
      }
      setEditActivity(response.activity);
    } catch (error) {
      console.error("Error updating activity:", error);
      toast.error("Failed to update activity");
    }
  };

  const handleDescriptionUpdate = async () => {
    if (!activity || !currentUser?.id) return;

    try {
      const newLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser,
        changeDescription: "Updated activity description"
      };

      const updatedLog = activity.activityLogs ? [...activity.activityLogs, newLogEntry] : [newLogEntry];
      const logUpdate: Partial<Activity> = { activityLogs: updatedLog, description };
      await updateActivity(procedure.id, activity.id, logUpdate);
      toast.success("Activity description updated successfully");
      setEditDescription(false);
    } catch (error) {
      console.error("Error updating description:", error);
      toast.error("Failed to update description");
    }
  };

  const handleSystemsUpdate = async () => {
    if (!activity || !currentUser?.id) return;

    try {
      const newLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser,
        changeDescription: "Updated systems used"
      };

      const updatedLog = activity.activityLogs ? [...activity.activityLogs, newLogEntry] : [newLogEntry];
      const update: Partial<Activity> = {
        systemsUsed: selectedSystems,
        activityLogs: updatedLog
      };

      await updateActivity(procedure.id, activity.id, update);
      setEditSystems(false);
      toast.success("Systems updated successfully");
    } catch (error) {
      console.error("Error updating systems:", error);
      toast.error("Failed to update systems");
    }
  };

  const handleAddLink = async () => {
    if (!activity || !currentUser?.id) return;

    try {

      const newLinkEntry: ActivityLink = {
        id: Date.now().toString(),
        title: newLink.title,
        url: newLink.url,
        description: newLink.description
      };

      const newLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser,
        changeDescription: `Added link: ${newLink.title}`
      };

      const updatedLog = activity.activityLogs ? [...activity.activityLogs, newLogEntry] : [newLogEntry];
      const logUpdate: Partial<Activity> = { activityLogs: updatedLog };
      await addActivityLink(activity.id, newLinkEntry);
      // await updateActivity(procedure.id, activity.id, logUpdate);

      setNewLink({ title: "", url: "", description: "" });
      setOpenAddLink(false);
      toast.success("Link added successfully");
    } catch (error) {
      console.error("Error adding link:", error);
      toast.error("Failed to add link");
    }
  };

  const handleEditLink = async () => {
    if (!activity || !currentUser?.id || !editingLink) return;

    try {
      const newLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser,
        changeDescription: `Updated link: ${editingLink.title}`
      };

      const updatedLog = activity.activityLogs ? [...activity.activityLogs, newLogEntry] : [newLogEntry];
      const logUpdate: Partial<Activity> = { activityLogs: updatedLog };
      await updateActivity(procedure.id, activity.id, logUpdate);
      await updateActivityLink(activity.id, editingLink);

      setEditingLink(null);
      setOpenEditLink(false);
      toast.success("Link updated successfully");
    } catch (error) {
      console.error("Error updating link:", error);
      toast.error("Failed to update link");
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!activity || !currentUser?.id) return;

    try {
      const linkToDelete = activity.links?.find(link => link.id == linkId);
      if (!linkToDelete) return;


      // await updateActivity(procedure.id, activity.id);
      await deleteActivityLink(activity.id, linkId);

      toast.success("Link deleted successfully");
    } catch (error) {
      console.error("Error deleting link:", error);
      toast.error("Failed to delete link");
    }
  };

  const handleHowToUpdate = async () => {
    if (!activity || !currentUser?.id) return;

    try {
      const newLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser,
        changeDescription: "Updated how-to instructions"
      };

      const updatedLog = activity.activityLogs ? [...activity.activityLogs, newLogEntry] : [newLogEntry];
      const logUpdate: Partial<Activity> = { activityLogs: updatedLog, howTo };
      await updateActivity(procedure.id, activity.id, logUpdate);
      toast.success("Activity how to updated successfully");
      setEditHowTo(false);
    } catch (error) {
      console.error("Error updating how-to:", error);
      toast.error("Failed to update how-to instructions");
    }
  };

  // Helper function to get user ID from either string or User object
  const getUserId = (user: string | UserType | null | undefined): string => {
    if (!user) return "";
    if (typeof user === "string") return user;
    return user.id;
  };

  // Helper function to render RACI assignment
  const renderRaciAssignment = (assignment: { type: string; data: any } | null) => {
    if (!assignment) return <span>No one assigned</span>;

    switch (assignment.type) {
      case 'user':
        return (
          <RouterLink to={`/people/${assignment.data.id}`}>
            <div className="hover:underline">{assignment.data.name}</div>
          </RouterLink>
        );
      case 'role':
        return (
          <RouterLink to={`/job-roles/${assignment.data.id}`}>
            <div className="hover:underline">{`${assignment.data.title} (Role)`}</div>
          </RouterLink>
        );
      case 'partner':
        return <div>{`${assignment.data.name} (External)`}</div>;
      default:
        return <span>Unknown assignment</span>;
    }
  };

  const handleAttachmentsChange = (newAttachments: any[]) => {
    setAttachments(newAttachments);
    if (activity) {
      setActivity({ ...activity, attachments: newAttachments });
    }
  };

  const setSelectedPerson = (prop: string, value: string) => {
    const [type, id] = value.split("-");
    setRaci({ ...raci, [prop]: { id, type: PersonType[type as keyof typeof PersonType] } });
  }

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              {activity.activityName}
              <Badge variant="outline" className="bg-amber-100 border-amber-200 text-amber-800 hover:bg-amber-100">
                Activity
              </Badge>
            </h1>
          </div>
        </div>

        <div className="hidden lg:flex gap-2 flex-shrink-0">
          <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
            <div className="flex gap-2">
              <Dialog open={openEdit} onOpenChange={setOpenEdit}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Activity
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl" aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>Edit Activity</DialogTitle>
                    <DialogDescription>
                      Update the activity details and RACI assignments.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="activityName">Activity Name</Label>
                      <Input
                        id="activityName"
                        value={editActivity?.activityName}
                        onChange={(e) => editActivity && setEditActivity({ ...editActivity, activityName: e.target.value })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="responsiblePerson">Responsible Person (R)</Label>
                      <Select
                        // value={getRaciId(editActivity?.responsiblePerson) || ""}
                        // value={`user-${(raci?.responsiblePerson?.id || "none").toString()}`}
                        value={
                          raci?.responsiblePerson
                            ? `${raci.responsiblePerson.type}-${raci.responsiblePerson.id}`
                            : 'user-none'
                        }
                        // onValueChange={(value: string) => editActivity && setEditActivity({...editActivity, responsiblePerson: getUser(users, value)})}
                        onValueChange={(value: string) => handleRaciSelect('responsiblePerson', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select responsible person" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user-none">None</SelectItem>
                          {/* Users Group */}
                          <SelectItem value="users-group" disabled>Users</SelectItem>
                          {filteredUsers.map((user) => (
                            <SelectItem key={`User-${user.id}`} value={`User-${user.id}`}>
                              {user.name} ({user.jobRole})
                            </SelectItem>
                          ))}
                          {/* Job Roles Group */}
                          <SelectItem value="roles-group" disabled>Job Roles</SelectItem>
                          {jobRoles.map((role) => (
                            <SelectItem key={`JobRole-${role.id}`} value={`JobRole-${role.id}`}>
                              {role.title} (Role)
                            </SelectItem>
                          ))}
                          {/* External Partners Group */}
                          <SelectItem value="partners-group" disabled>External Partners</SelectItem>
                          {externalPartners.map((partner) => (
                            <SelectItem key={`ExternalPartner-${partner.id}`} value={`ExternalPartner-${partner.id}`}>
                              {partner.name} (External)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Select the person, role, or partner who will be responsible for this activity
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="accountablePerson">Accountable Person (A)</Label>
                      <Select
                        // value={getRaciId(editActivity?.accountablePerson) || ""}
                        // value={`user-${(raci?.accountablePerson?.id || "none").toString()}`}
                        value={
                          raci?.accountablePerson
                            ? `${raci.accountablePerson.type}-${raci.accountablePerson.id}`
                            : 'user-none'
                        }
                        // onValueChange={(value: string) => editActivity && setEditActivity({...editActivity, accountablePerson: getUser(users, value)})}
                        onValueChange={(value: string) => handleRaciSelect('accountablePerson', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select accountable person" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user-none">None</SelectItem>
                          {/* Users Group */}
                          <SelectItem value="users-group" disabled>Users</SelectItem>
                          {filteredUsers.map((user) => (
                            <SelectItem key={`User-${user.id}`} value={`User-${user.id}`}>
                              {user.name} ({user.jobRole})
                            </SelectItem>
                          ))}
                          {/* Job Roles Group */}
                          <SelectItem value="roles-group" disabled>Job Roles</SelectItem>
                          {jobRoles.map((role) => (
                            <SelectItem key={`JobRole-${role.id}`} value={`JobRole-${role.id}`}>
                              {role.title} (Role)
                            </SelectItem>
                          ))}
                          {/* External Partners Group */}
                          <SelectItem value="partners-group" disabled>External Partners</SelectItem>
                          {externalPartners.map((partner) => (
                            <SelectItem key={`ExternalPartner-${partner.id}`} value={`ExternalPartner-${partner.id}`}>
                              {partner.name} (External)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Select the person, role, or partner who will be accountable for this activity
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="consultedPerson">Consulted Person (C)</Label>
                      <MultiSelect
                        options={[
                          ...filteredUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                          ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                          ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                        ]}
                        selected={
                          // This correctly handles the array type
                          (raci?.consultedPerson ?? []).map(p => `${p.type}-${p.id}`)
                        }
                        onChange={(selectedValues: string[]) => handleRaciSelect('consultedPerson', selectedValues)}
                        placeholder="Select consulted parties"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Select the person, role, or partner who should be consulted for this activity
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="informedPerson">Informed Person (I)</Label>
                      <MultiSelect
                        options={[
                          ...filteredUsers.map(user => ({ value: `User-${user.id}`, label: `${user.name} (${user.jobRole})` })),
                          ...jobRoles.map(role => ({ value: `JobRole-${role.id}`, label: `${role.title} (Role)` })),
                          ...externalPartners.map(partner => ({ value: `ExternalPartner-${partner.id}`, label: `${partner.name} (External)` }))
                        ]}
                        selected={
                          // This correctly handles the array type
                          (raci?.informedPerson ?? []).map(p => `${p.type}-${p.id}`)
                        }
                        onChange={(selectedValues: string[]) => handleRaciSelect('informedPerson', selectedValues)}
                        placeholder="Select informed parties"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Select the person, role, or partner who should be informed about this activity
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline" onClick={() => setEditActivity(activity)}>Cancel</Button>
                    </DialogClose>
                    <Button onClick={handleUpdate}>Save Changes</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </Can>
        </div>
      </div>

      {/* Mobile Actions Dropdown */}
      <div className="lg:hidden">
        <Can I={ACTIONS.UPDATE} a={RESOURCES.PROCEDURE_ACTIVITY}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="w-full sm:w-auto">
                <MoreVertical className="w-4 h-4 mr-2" />
                Actions
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {canEdit && (
                <>
                  <DropdownMenuItem onClick={() => setOpenEdit(true)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Activity
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </Can>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
          <TabsTrigger
            value="activity"
            className="rounded-full px-4 py-2 data-[state=active]:bg-white"
          >
            Details
          </TabsTrigger>
          <TabsTrigger
            value="log"
            className="rounded-full px-4 py-2 data-[state=active]:bg-white"
          >
            Activity Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="space-y-6 mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Activity Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Activity Name</dt>
                    <dd>{activity.activityName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Part of Procedure</dt>
                    <dd>
                      <RouterLink to={`/procedure/${procedure.id}`}>
                        <div className="flex items-center gap-2 hover:underline">
                          <FileText size={16} />
                          {procedure.procedureName}
                        </div>
                      </RouterLink>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Created On</dt>
                    <dd>
                      {toSydneyDate(activity.createdAt)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Created By</dt>
                    <dd>{creator ? creator.name : "Unknown"}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">RACI Assignments</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Responsible (R)</dt>
                    <dd className="flex items-center gap-2">
                      <UserIcon size={16} />
                      {renderRaciParty(activity.raci?.responsiblePerson)}
                    </dd>
                    <dd className="text-xs text-muted-foreground mt-1">
                      The person who does the work to complete the task
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Accountable (A)</dt>
                    <dd className="flex items-center gap-2">
                      <UserIcon size={16} />
                      {renderRaciParty(activity.raci?.accountablePerson)}
                    </dd>
                    <dd className="text-xs text-muted-foreground mt-1">
                      The person who is ultimately answerable for the task
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Consulted (C)</dt>
                    <dd className="flex items-center gap-2">
                      {renderRaciArray(activity.raci?.consultedPerson)}
                    </dd>
                    <dd className="text-xs text-muted-foreground mt-1">
                      People whose opinions are sought before executing the task
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Informed (I)</dt>
                    <dd className="flex items-center gap-2">
                      {renderRaciArray(activity.raci?.informedPerson)}
                    </dd>
                    <dd className="text-xs text-muted-foreground mt-1">
                      People who are kept up-to-date on progress
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          </div>

          {/* Systems Used Section - Temporarily Hidden */}
          {/* <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Systems Used</CardTitle>
          {canEdit && !editSystems && (
            <Button variant="outline" size="sm" onClick={() => setEditSystems(true)}>
              <Edit size={16} className="mr-2" />
              Edit Systems
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {editSystems ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="systems">Select Systems</Label>
                <MultiSelect
                  options={activeSystems.map(system => ({ value: system.name, label: system.name }))}
                  selected={selectedSystems}
                  onChange={setSelectedSystems}
                  placeholder="Select systems used in this activity"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditSystems(false)}>Cancel</Button>
                <Button onClick={handleSystemsUpdate}>Save Systems</Button>
              </div>
            </div>
          ) : (
            <div>
              {activity.systemsUsed && activity.systemsUsed.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {activity.systemsUsed.map((system: string, index: number) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      <Monitor size={12} className="mr-1" />
                      {system}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground italic">No systems selected for this activity.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card> */}

          {/* Two Column Layout for Why this is important and Links */}
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {/* Why this is important Section */}
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {/* <span className="font-heading text-lg font-semibold">Why</span> */}
                    <span className="text-lg font-semibold text-muted-foreground">Why is this Activity important</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Help your team understand the value. What risk does it reduce? What benefit does it bring?
                  </p>
                </div>
                {!editDescription && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.ACTIVITY_IMPORTANCE}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => setEditDescription(true)}>
                          <Edit size={16} />
                          <span className="sr-only">Edit Description</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit Description</p>
                      </TooltipContent>
                    </Tooltip>
                  </Can>
                )}
              </CardHeader>
              <CardContent>
                {editDescription ? (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Textarea
                        placeholder="Explain why this activity is important and its impact on the process..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="min-h-32"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (e.ctrlKey || e.metaKey) {
                              e.preventDefault();
                              handleDescriptionUpdate();
                            }
                          }
                        }}
                      />
                      <div className="flex justify-between text-sm text-muted-foreground px-1">
                        <span>New line (Enter)</span>
                        <span>Save (Ctrl + Enter)</span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setEditDescription(false)}>Cancel</Button>
                      <Button onClick={handleDescriptionUpdate}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {activity.description ? (
                      <p className="whitespace-pre-wrap">{activity.description}</p>
                    ) : (
                      <p className="text-muted-foreground italic">No explanation provided for why this activity is important.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            {/* Links Section */}
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <CardTitle className="text-lg font-semibold text-muted-foreground">Links</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Add any link to tools, templates, or resources that make this step easier to complete.
                  </p>
                </div>
                <Can I={ACTIONS.UPDATE} a={RESOURCES.ACTIVITY_LINKS}>
                  <Dialog open={openAddLink} onOpenChange={setOpenAddLink}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="icon" className="flex-shrink-0">
                            <Plus size={16} />
                            <span className="sr-only">Add Link</span>
                          </Button>
                        </DialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Add Link</p>
                      </TooltipContent>
                    </Tooltip>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Link</DialogTitle>
                        <DialogDescription>
                          Add a URL reference related to this activity.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="linkTitle">Title</Label>
                          <Input
                            id="linkTitle"
                            placeholder="e.g. Company Wiki"
                            value={newLink.title}
                            onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                handleAddLink();
                              }
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="linkUrl">URL</Label>
                          <Input
                            id="linkUrl"
                            placeholder="e.g. https://example.com"
                            value={newLink.url}
                            onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                handleAddLink();
                              }
                            }}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="linkDescription">Description (Optional)</Label>
                          <Textarea
                            id="linkDescription"
                            placeholder="Add a brief description of this link..."
                            value={newLink.description}
                            onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                            className="resize-none"
                            rows={3}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                handleAddLink();
                              }
                            }}
                          />
                        </div>
                        <div className="flex justify-end text-sm text-muted-foreground px-1">
                          <span>Save (Ctrl + Enter)</span>
                        </div>
                      </div>
                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button onClick={handleAddLink}>Add Link</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </Can>
              </CardHeader>
              <CardContent>
                {activity.links && activity.links.length > 0 ? (
                  <ul className="space-y-3">
                    {activity.links.map((link: ActivityLink) => (
                      <li key={link.id} className="flex items-start justify-between">
                        <div className="space-y-1">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-primary hover:underline"
                          >
                            <Link2 size={16} />
                            <span>{link.title}</span>
                            <ExternalLink size={14} className="text-muted-foreground" />
                          </a>
                          {link.description && (
                            <p className="text-sm text-muted-foreground ml-6">{link.description}</p>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-2">
                            <Dialog open={openEditLink && editingLink?.id === link.id} onOpenChange={(open) => {
                              setOpenEditLink(open);
                              if (open) setEditingLink(link);
                            }}>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="flex items-center gap-1">
                                  <Edit size={16} className="text-muted-foreground" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Link</DialogTitle>
                                  <DialogDescription>
                                    Modify the link details.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label htmlFor="editLinkTitle">Title</Label>
                                    <Input
                                      id="editLinkTitle"
                                      value={editingLink?.title || ""}
                                      onChange={(e) => {
                                        if (editingLink) {
                                          setEditingLink({
                                            ...editingLink,
                                            title: e.target.value
                                          });
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                          e.preventDefault();
                                          handleEditLink();
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="editLinkUrl">URL</Label>
                                    <Input
                                      id="editLinkUrl"
                                      value={editingLink?.url || ""}
                                      onChange={(e) => {
                                        if (editingLink) {
                                          setEditingLink({
                                            ...editingLink,
                                            url: e.target.value
                                          });
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                          e.preventDefault();
                                          handleEditLink();
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="editLinkDescription">Description (Optional)</Label>
                                    <Textarea
                                      id="editLinkDescription"
                                      placeholder="Add a brief description of this link..."
                                      value={editingLink?.description || ""}
                                      onChange={(e) => {
                                        if (editingLink) {
                                          setEditingLink({
                                            ...editingLink,
                                            description: e.target.value
                                          });
                                        }
                                      }}
                                      className="resize-none"
                                      rows={3}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                          e.preventDefault();
                                          handleEditLink();
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="flex justify-end text-sm text-muted-foreground px-1">
                                    <span>Save (Ctrl + Enter)</span>
                                  </div>
                                </div>
                                <DialogFooter>
                                  <DialogClose asChild>
                                    <Button variant="outline">Cancel</Button>
                                  </DialogClose>
                                  <Button onClick={handleEditLink}>Save Changes</Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="flex items-center gap-1"
                              onClick={() => handleDeleteLink(link.id)}
                            >
                              <Trash2 size={16} className="text-destructive" />
                            </Button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground italic">No links added yet.</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* How to Complete Section - Full Width */}
          <div className="mt-6">
            <Card>
              <CardHeader className="flex flex-row items-start justify-between gap-4 p-4 sm:p-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {/* <span className="font-heading text-lg font-semibold">How</span> */}
                    <span className="text-lg font-semibold text-muted-foreground">How to complete this activity</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Step-by-step guide to get it done. Use clear language and keep it actionable.
                  </p>
                </div>
                {!editHowTo && (
                  <Can I={ACTIONS.UPDATE} a={RESOURCES.ACTIVITY_INSTRUCTIONS}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline" size="icon" className="flex-shrink-0" onClick={() => setEditHowTo(true)}>
                          <Edit size={16} />
                          <span className="sr-only">Edit</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Edit How to</p>
                      </TooltipContent>
                    </Tooltip>
                  </Can>
                )}
              </CardHeader>
              <CardContent>
                {editHowTo ? (
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <div
                        onKeyDown={(e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            handleHowToUpdate();
                          }
                        }}
                      >
                        {/* <RichTextEditor
                      value={howTo}
                      onChange={setHowTo}
                      placeholder="Provide step-by-step instructions or important notes for completing this activity..."
                      className="min-h-32"
                    /> */}
                        <CustomEditor
                          value={howTo}
                          onChange={setHowTo}
                          placeholder="Provide step-by-step instructions or important notes for completing this activity..."
                        />
                      </div>
                      <div className="flex justify-end text-sm text-muted-foreground px-1">
                        <span>Save (Ctrl + Enter)</span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => { setEditHowTo(false); setHowTo(activity.howTo || ""); }}>Cancel</Button>
                      <Button onClick={handleHowToUpdate}>Save</Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    {activity.howTo ? (
                      <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: activity.howTo }} />
                    ) : (
                      <p className="text-muted-foreground italic">No instructions provided for how to complete this activity.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Attachments */}
          <FileAttachments
            resourceType="activities"
            resourceId={activity.id}
            attachments={attachments}
            onAttachmentsChange={handleAttachmentsChange}
            // canEdit={canEdit}
            disabled={false}
          />
        </TabsContent>

        <TabsContent value="log" className="space-y-6 mt-6">
          <ActivityLog
            activityLog={activity.activityLogs}
            users={users}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
